import { useState } from 'react';
import { Link } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import Seo from '@/components/feature/Seo';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import { faqs } from '@/mocks/faqs';
import { siteInfo } from '@/mocks/site';

export default function FaqPage() {
  const [openIndex, setOpenIndex] = useState<number>(0);

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((item) => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: { '@type': 'Answer', text: item.answer },
      })),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: '/' },
        { '@type': 'ListItem', position: 2, name: '常见问题', item: '/faq' },
      ],
    },
  ];

  return (
    <SiteLayout>
      <Seo
        title="常见问题｜深夜爆料 投稿与更新说明"
        description="深夜爆料的更新频率、首发判断方式、投稿要求、更正流程与转载规范，都在这一页说明。投稿默认匿名处理，编辑通常在 48 小时内回复。"
        keywords="深夜爆料常见问题,深夜爆料投稿,深夜爆料更新频率,深夜爆料核实,新瓜速递"
        path="/faq"
        ogType="website"
        jsonLd={jsonLd}
      />

      <div className="mx-auto w-full max-w-4xl px-4 py-8 md:px-10 md:py-12">
        <Breadcrumbs items={[{ label: '常见问题' }]} />

        <header className="mb-10">
          <span className="text-xs tracking-[0.24em] text-primary-400">FAQ</span>
          <h1 className="mt-4 font-heading text-3xl font-black leading-tight text-foreground-50 md:text-4xl">
            关于深夜爆料，你可能想先确认的几件事
          </h1>
          <p className="mt-4 text-sm leading-relaxed text-foreground-400 md:text-base">
            这里整理了读者和投稿人问得最多的八个问题。如果看完还有疑问，欢迎直接联系我们，编辑在线时段为 {siteInfo.contactHours}。
          </p>
        </header>

        <div className="space-y-3">
          {faqs.map((item, index) => {
            const open = openIndex === index;
            return (
              <div
                key={item.question}
                className={`overflow-hidden rounded-lg border transition-colors ${
                  open ? 'border-primary-500/40 bg-background-100' : 'border-background-200 bg-background-100/60'
                }`}
              >
                <h2>
                  <button
                    type="button"
                    onClick={() => setOpenIndex(open ? -1 : index)}
                    aria-expanded={open}
                    className="flex w-full cursor-pointer items-center justify-between gap-4 px-5 py-4 text-left"
                  >
                    <span className="flex items-start gap-3">
                      <span
                        className={`mt-0.5 font-heading text-sm font-bold ${
                          open ? 'text-primary-400' : 'text-background-400'
                        }`}
                      >
                        {String(index + 1).padStart(2, '0')}
                      </span>
                      <span className="font-heading text-base font-semibold text-foreground-100">
                        {item.question}
                      </span>
                    </span>
                    <i
                      className={`shrink-0 text-xl text-foreground-500 transition-transform ${
                        open ? 'ri-subtract-line rotate-180 text-primary-400' : 'ri-add-line'
                      }`}
                    ></i>
                  </button>
                </h2>
                <div
                  className={`grid transition-all duration-300 ${
                    open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
                  }`}
                >
                  <div className="overflow-hidden">
                    <p className="px-5 pb-5 pl-[52px] text-sm leading-relaxed text-foreground-400">
                      {item.answer}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <section className="mt-14 rounded-lg border border-background-200 bg-background-100 p-6 md:p-8">
          <h2 className="font-heading text-xl font-bold text-foreground-50">
            还有没解答的问题？
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-foreground-400">
            投稿、更正、授权与商务合作都可以通过同一张表单提交，我们会按提交顺序处理。你也可以直接写信到 {siteInfo.email}。
          </p>
          <div className="mt-6 flex flex-col gap-3 sm:flex-row">
            <Link
              to="/contact"
              className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-950 transition-colors hover:bg-primary-400"
            >
              <i className="ri-send-plane-fill"></i>
              提交你的问题
            </Link>
            <Link
              to="/about"
              className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300/70 px-5 py-2.5 text-sm text-foreground-200 transition-colors hover:border-primary-500/60 hover:text-primary-300"
            >
              <i className="ri-information-line"></i>
              了解我们的内容原则
            </Link>
          </div>
        </section>
      </div>
    </SiteLayout>
  );
}